/**
 * CopyId — small "Copy ID" affordance for record identifiers.
 *
 * Only reports "Copied" after the clipboard write actually succeeded;
 * a failure (no clipboard API, permission denied, insecure context without
 * a working fallback) is surfaced as "Copy failed" instead of a false success.
 */
import { useEffect, useRef, useState } from 'react';
import { cn } from '../lib/utils';

type CopyState = 'idle' | 'copied' | 'failed';

async function copyToClipboard(value: string): Promise<boolean> {
  if (
    typeof navigator !== 'undefined' &&
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === 'function'
  ) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      /* fall through to the legacy path */
    }
  }
  // Legacy fallback for insecure contexts (plain http on a LAN) and old WebViews.
  if (typeof document === 'undefined' || typeof document.execCommand !== 'function') return false;
  try {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.setAttribute('aria-hidden', 'true');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

export function CopyId({
  value,
  className,
  label = 'Copy ID',
}: {
  value: string;
  className?: string;
  /** Visible idle label. Defaults to "Copy ID". */
  label?: string;
}) {
  const [state, setState] = useState<CopyState>('idle');
  const resetTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (resetTimer.current !== undefined) window.clearTimeout(resetTimer.current);
    };
  }, []);

  const text = state === 'copied' ? 'Copied' : state === 'failed' ? 'Copy failed' : label;

  return (
    <button
      type="button"
      title={`${label}: ${value}`}
      aria-live="polite"
      onClick={async (event) => {
        event.preventDefault();
        event.stopPropagation();
        const ok = await copyToClipboard(value);
        setState(ok ? 'copied' : 'failed');
        if (resetTimer.current !== undefined) window.clearTimeout(resetTimer.current);
        resetTimer.current = window.setTimeout(() => setState('idle'), ok ? 1200 : 2500);
      }}
      className={cn(
        'rounded text-[10px] font-medium text-text-faint hover:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        state === 'failed' && 'text-error hover:text-error',
        className,
      )}
    >
      {text}
    </button>
  );
}
